import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CashboxOpeningEntry } from '@common/models/cashbox.model';
import { AnalyticsService, CashboxService, ToastService } from '@common/services';
import { UserService } from '@modules/auth/services';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { take } from 'rxjs';

/** Una fila del modal: la cuenta que viene del api más lo que el usuario decide. */
interface OpeningRow extends CashboxOpeningEntry {
    selected: boolean;
    initialAmount: number | null;
    badInput?: boolean;
}

@Component({
    selector: 'fina-cashbox-open',
    templateUrl: './cashbox-open.component.html',
    styleUrls: ['./cashbox-open.component.scss'],
})
export class CashboxOpenComponent implements OnInit, OnDestroy {
    rows: OpeningRow[] = [];
    missingPaymentMethodNames: string[] = [];
    loading = true;
    saving = false;
    loadError = false;

    constructor(
        public activeModal: NgbActiveModal,
        public cashboxService: CashboxService,
        private analyticsService: AnalyticsService,
        public userService: UserService,
        private toastService: ToastService,
        private modalService: NgbModal,
        private router: Router,
    ) {}

    ngOnInit() {
        this.loadOpeningInfo();
    }

    ngOnDestroy(): void {
        this.cashboxService.openedFromScFinish = false;
        this.modalService.dismissAll();
    }

    loadOpeningInfo() {
        this.loading = true;
        this.cashboxService
            .getOpeningInfo$()
            .pipe(take(1))
            .subscribe({
                next: info => {
                    // El monto arranca vacío y el 0 es solo placeholder, igual que el contado del cierre.
                    this.rows = (info.entries || []).map(entry => ({ ...entry, selected: true, initialAmount: null }));
                    this.missingPaymentMethodNames = (info.missingPaymentMethods || []).map(pm => pm.name).filter(Boolean);
                    this.loading = false;
                },
                error: err => {
                    this.loading = false;
                    this.loadError = true;
                    this.toastService.error(err.error?.title || 'Error', err.error?.message || 'No se pudieron cargar los métodos de pago del cierre');
                },
            });
    }

    get selectedRows(): OpeningRow[] {
        return this.rows.filter(row => row.selected);
    }

    get hasSelection(): boolean {
        return this.selectedRows.length > 0;
    }

    /**
     * Totales de apertura por moneda. No se suman entre sí: cada moneda cuadra por separado,
     * igual que en el cierre.
     */
    get openingTotals(): Array<{ currency: string; total: number }> {
        const totals = new Map<string, number>();
        for (const row of this.selectedRows) {
            totals.set(row.currency, (totals.get(row.currency) || 0) + (Number(row.initialAmount) || 0));
        }
        return [...totals.entries()].map(([currency, total]) => ({ currency, total }));
    }

    get hasInvalidAmount(): boolean {
        // El campo vacío vale como 0 al abrir; bloquean los negativos y el texto inválido
        // (que el input numérico también entrega como null, pero no debe abrirse como 0).
        return this.selectedRows.some(row => row.initialAmount < 0 || row.badInput);
    }

    onToggleRow(row: OpeningRow) {
        row.selected = !row.selected;
        // Una cuenta que no participa no aporta monto de apertura.
        if (!row.selected) {
            row.initialAmount = null;
            row.badInput = false;
        }
    }

    // Limita el monto a 10 enteros y 2 decimales, igual que el modal anterior.
    validateAmount(event: any, row: OpeningRow) {
        row.badInput = !!event.target.validity?.badInput;
        const parts = String(event.target.value).split('.');
        if (parts[0] && parts[0].length >= 11) {
            event.target.value = parts[0].substring(0, 10) + (parts[1] ? '.' + parts[1] : '');
            row.initialAmount = parseFloat(event.target.value);
        }
        if (parts[1] && parts[1].length > 2) {
            event.target.value = parts[0] + '.' + parts[1].substring(0, 2);
            row.initialAmount = parseFloat(event.target.value);
        }
    }

    openCashbox() {
        if (!this.hasSelection || this.hasInvalidAmount || this.saving) return;

        this.saving = true;
        const entries = this.selectedRows.map(row => ({
            bankAccountId: row.bankAccountId,
            selected: true,
            initialAmount: Number(row.initialAmount) || 0,
        }));

        this.cashboxService.createCashBox(entries).subscribe({
            next: createdCashbox => {
                this.analyticsService.analyticsCapture('cashbox_opened', { methods_count: entries.length });
                this.cashboxService.openedFromScFinish = false;
                this.saving = false;
                this.activeModal.close(createdCashbox);
            },
            error: err => {
                // El modal se queda abierto: la apertura pide un monto por cada método y
                // cerrarlo ante un error de red obligaría a volver a escribirlos todos.
                // El toast avisa qué pasó y saving = false rehabilita el botón para reintentar.
                this.saving = false;
                this.toastService.error(err.error?.title || 'Error', err.error?.message || 'No se pudo abrir la caja');
            },
        });
    }

    closeModal() {
        this.cashboxService.openedFromScFinish = false;
        this.activeModal.dismiss('cancel');
    }

    /** Lleva a la configuración para agregar métodos; la caja se abre después. */
    goToConfig() {
        this.cashboxService.openedFromScFinish = false;
        this.activeModal.dismiss('cancel');
        this.router.navigate(['/comercio/cashbox']);
    }

    @HostListener('window:popstate', ['$event'])
    onPopState(event: any) {
        this.modalService.dismissAll();
    }

    @HostListener('window:keydown.esc', ['$event'])
    onEscKeydown(event: KeyboardEvent) {
        this.closeModal();
    }
}
